/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { GTMServiceEvent, GTMServiceTrackContentViewEvent, GtmService } from '@df/module-gtm/gtm.service';
import { isCheckoutUrl } from '../../ui/common/if-checkout-process.service';
import IContentViewData = Gtm.IContentViewData;

declare module '@df/module-gtm/gtm.namespace' {
    namespace Gtm {
        export interface IEventAddShipmentInfoData {
            cc_store?: string;
        }
    }
}

declare module '@df/module-gtm/gtm.service' {
    export interface IContentViewData {
        pdp_category?: string;
        plp_category?: string;
    }
}

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        export interface ICustomerData {
            external_id?: string;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class GtmServiceDecorator extends GtmService {
    protected router = inject(Router);

    protected override setAddPaymentInfoTracking!: () => void;
    protected override setAddShipmentInfoTracking!: () => void;
    private _lastPaymentMethod?: string | number;
    private _lastShipmentMethod?: string | number;

    protected override setListeners() {
        this.setAddPaymentInfoTracking = this.timeoutService.throttle(() => this.setAddPaymentInfoTrackingThrottled(), 1000, {
            leading: false,
            trailing: true
        });
        this.setAddShipmentInfoTracking = this.timeoutService.throttle(() => this.setAddShipmentInfoTrackingThrottled(), 1000, {
            leading: false,
            trailing: true
        });

        super.setListeners();

        this.subscriptions.add(
            this.events.subscribe((routerEvent: GTMServiceEvent) => {
                if (routerEvent instanceof GTMServiceTrackContentViewEvent) {
                    this.extendContentViewData(routerEvent);
                }
            })
        );
    }

    extendContentViewData(routerEvent: GTMServiceTrackContentViewEvent): void {
        const eventData = routerEvent.eventData;
        const model = routerEvent.model;

        if (model && model.data) {
            if (model.data.type === 'product') {
                eventData['content-data'] = <IContentViewData>{
                    pdp_category: (<IProductDataV3>model.data).category_path,
                    ...eventData['content-data']
                };
            }

            if (model.data.type === 'category') {
                eventData['content-data'] = <IContentViewData>{
                    plp_category: (<ICategoryDataV3>model.data)?.breadcrumbs?.[0]?.label,
                    ...eventData['content-data']
                };
            }
        }
    }

    publicTrackContentView(path: string, model: any, metaData?: any): void {
        this.trackContentView(path, model, metaData);
    }

    protected override getEcommerceItemsFromBasketValue(): number {
        return this.customer.basket.totals?.subtotal;
    }

    override trackStep(stepName: string, checkout) {
        if (this.preventCheckoutTracking()) {
            return;
        }

        return super.trackStep(stepName, checkout);
    }

    override trackCheckoutOption(step: number, option: string) {
        // do nothing
    }

    override trackCheckoutStep(step: number, option?: string): void {
        if (this.preventCheckoutTracking()) {
            return;
        }

        return super.trackCheckoutStep(step, option);
    }

    preventCheckoutTracking(): boolean {
        return !isCheckoutUrl(this.router.url);
    }

    /**
     * Get begin checkout event data.
     *
     * @info Ready for GA4
     */
    protected override getSaveShipmentMethodBeforeData(data: Checkout.ISaveShippingMethodEventData): Gtm.IEventAddShipmentInfo {
        const eventData = super.getSaveShipmentMethodBeforeData(data);

        if (data.additionalData?.['store_name']) {
            eventData.ecommerce.cc_store = data.additionalData['store_name'];
        }

        return eventData;
    }

    /**
     * Set up listener for add payment info on method selection, not on save
     */
    protected setAddPaymentInfoTrackingThrottled(): void {
        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.PickPaymentMethod).subscribe((data: Checkout.ISavePaymentMethodEventData) => {
                if (this._lastPaymentMethod !== data.method) {
                    this.trackSavePaymentMethodBefore(data);

                    this._lastPaymentMethod = data.method;
                }
            })
        );
    }

    /**
     * Set up listener for add shipment info on method selection, not on save
     */
    protected setAddShipmentInfoTrackingThrottled(): void {
        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.PickShippingMethod).subscribe((data: Checkout.ISaveShippingMethodData) => {
                if (this._lastShipmentMethod !== data.method) {
                    this.trackSaveShipmentMethodBefore(data);

                    this._lastShipmentMethod = data.method;
                }
            })
        );
    }

    // Extending each gtm event with additional data
    override push(eventData: Gtm.IEvent) {
        if (this.customerModel?.data?.external_id) {
            eventData['custom-dimensions'] = {
                'external-id': this.customerModel.data.external_id
            };
        }

        super.push(eventData);
    }
}
